<template>
  <v-flex md12 v-if="fines && fines.length > 0">
    <v-divider class="mt-3 mb-3"></v-divider>
    <h2>{{ $t("custom.fines") }}</h2>
    <v-data-table
      :headers="fines_headers"
      :loading="isLoading"
      :loading-text="$t('custom.loading_table')"
      :items="fines"
      :sort-by="['created_at']"
      :sort-desc="[true]"
      mobile-breakpoint="100"
      dense
      hide-default-footer
    >
      <template slot="item" slot-scope="props">
        <tr>
          <td class="text-center">
            <router-link :to="{ path: `/fines/${props.item.number}` }">{{
              props.item.number
              }}</router-link>
          </td>
          <td class="text-center">
            {{ returnDate(props.item.date) }}
          </td>
          <td class="text-center">
            <div>{{ props.item.sum }}</div>
            <div v-if="props.item.client_payment_method === 'deposit_hold'" class="caption">
              {{ $t("fines.payment_method.deposit_hold") }}:
              {{ $t("fines.paid") }} {{ props.item.client_paid_amount || 0 }},
              {{ $t("fines.charging") }} {{ props.item.client_charging_amount || 0 }},
              {{ $t("fines.unpaid") }} {{ props.item.client_unpaid_amount || 0 }}
            </div>
          </td>
          <td class="text-center description-trunc">
            <read-more v-if="props.item.description &&
              props.item.description.length > 0
            " :more-str="$t('custom.read')" :text="props.item.description" link="#" :less-str="$t('custom.less')"
              :max-chars="20"></read-more>
          </td>
          <!-- Состояние -->
          <td class="text-center">
            <v-btn :color="stateFineColor(props.item.state)" class="my-2" small block>
              {{ translateFinesState(props.item.state) }}
            </v-btn>
            <v-btn v-if="canWithholdFromDeposit(props.item)" x-small outlined color="warning"
              :loading="fineWithholdLoadingId === props.item.id" :disabled="fineWithholdLoadingId !== null"
              @click="startFineWithhold(props.item)" data-testid="fine-withhold-from-deposit">
              {{ $t("fines.withhold_from_deposit") }}
            </v-btn>
          </td>
        </tr>
      </template>
      <template slot="no-data">
        {{ $t("custom.no_data_in_table") }}
      </template>
    </v-data-table>
    <v-dialog v-model="fineWithholdDialog" :retain-focus="false" persistent max-width="520">
      <v-card>
        <v-card-title>{{ $t("fines.withhold_from_deposit") }}</v-card-title>
        <v-card-text>
          <v-text-field v-model.number="fineWithholdForm.sum" type="number" min="0"
            :label="$t('custom.amount')"></v-text-field>
          <v-text-field v-model.trim="fineWithholdForm.reason" :label="$t('bookings.deposit_hold.reason')"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="fineWithholdLoadingId !== null" @click="fineWithholdDialog = false">{{ $t("custom.cancel") }}</v-btn>
          <v-btn color="warning" :loading="fineWithholdLoadingId !== null"
            :disabled="!(Number(fineWithholdForm.sum) > 0 && fineWithholdForm.reason) || fineWithholdLoadingId !== null"
            @click="submitFineWithhold(selectedFine, fineWithholdForm)">{{ $t("fines.withhold_from_deposit") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-flex>
</template>

<script>
import moment from 'moment'
import 'moment/locale/ru'
import 'moment/locale/en-gb'
import { translateFineState as translateFineStateText } from "@/utils/localeInfra";

export default {
  name: 'BookingFines',
  props: {
    entity: {
      type: Boolean,
      required: false,
    },
    fines: {
      type: Array,
      required: true,
      default: () => []
    },
    fines_headers: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    depositHold: { type: Object, default: null }
  },
  data() {
    return {
      fineWithholdDialog: false,
      fineWithholdLoadingId: null,
      selectedFine: null,
      fineWithholdForm: { sum: null, reason: "" },
    };
  },
  methods: {
    canWithholdFromDeposit(fine) {
      const hold = this.depositHold;
      return !!(hold && hold.state === "open" && hold.secured && hold.actions && hold.actions.withhold &&
        fine && fine.client_payment_method !== "deposit_hold");
    },
    startFineWithhold(fine) {
      this.selectedFine = fine;
      this.fineWithholdForm = { sum: Number(fine.sum) || null, reason: fine.description || this.$t("fines.fine") };
      this.fineWithholdDialog = true;
    },
    submitFineWithhold(fine, form) {
      if (!fine || this.fineWithholdLoadingId !== null) return Promise.resolve();
      this.fineWithholdLoadingId = fine.id;
      const body = { sum: Number(form.sum), reason: String(form.reason || "").trim() };
      return this.axios.post(`/api/v1/fines/${fine.id}/withhold_from_deposit`, body, {
        headers: { Authorization: this.$store.getters.getAuthToken },
      }).then((response) => {
        const data = response.data || {};
        this.$set(fine, "client_payment_method", "deposit_hold");
        this.$set(fine, "client_paid_amount", Number(data.paid_from_deposit || 0) + Number(data.charged_from_card || 0));
        this.$set(fine, "client_charging_amount", Number(data.charging) || 0);
        this.$set(fine, "client_unpaid_amount", Number(data.unpaid) || 0);
        this.fineWithholdDialog = false;
        this.$emit("deposit-hold-updated", data.deposit_hold);
      }).catch((error) => this.setError(error, this.$t("errors.error")))
        .then(() => { this.fineWithholdLoadingId = null; });
    },
    returnDate(date) {
      if (date && moment(date).isValid()) {
        return moment(date).format('DD.MM.YYYY')
      } else {
        return date
      }
    },
    stateFineColor(state) {
      switch(state) {
        case 'paid':
          return 'success'
        case 'partial':
          return 'warning'
        default:
          return 'error'
      }
    },
    translateFinesState(state) {
      return translateFineStateText(this.$t.bind(this), state);
    },
  }
}
</script>
